import { Fragment, Suspense } from "react";
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  styled,
  Typography,
  Tooltip,
  TableContainer,
} from "@mui/material";
import { useCaseInfo } from "src/hooks/caseHooks";
import { CaseStatus, type CaseBase, type PollutantInfo } from "src/types/case";
import { round } from "lodash-es";
import { useTranslation } from "react-i18next";

interface Props {
  case: CaseBase;
  className?: string;
}

export function CaseOverviewDetailsTable(props: Props) {
  const { t } = useTranslation();
  const hasLoadData = props.case.nbScenarios > 1 && props.case.status === CaseStatus.CALCULATION_FINISHED;
  const hasContent = props.case.description || hasLoadData;

  return (
    <Styles className={props.className}>
      <TableCell colSpan={7} className="parent-cell">
        {props.case.description && (
          <Typography className="description" component="div" variant="body2" color="text.primary">
            {props.case.description}
          </Typography>
        )}{" "}
        {hasLoadData && (
          <Suspense fallback={<CircularProgress size={20} className="loading-spinner" />}>
            <LoadData {...props} />
          </Suspense>
        )}
        {!hasContent && (
          <Typography className="description" component="div" variant="body2" color="text.primary">
            {t("label.noFurtherInfoAvailableForThisCase")}
          </Typography>
        )}
      </TableCell>
    </Styles>
  );
}

interface GroupedPollutant {
  substanceId: number;
  normal?: PollutantInfo;
  relative?: PollutantInfo;
}

function groupPollutants(pollutantList: PollutantInfo[]): GroupedPollutant[] {
  const substanceIds = Array.from(new Set(pollutantList.map((p) => p.substanceId)));

  return substanceIds.map((substanceId) => {
    const pollutants = pollutantList.filter((p) => p.substanceId === substanceId);
    return {
      substanceId,
      normal: pollutants.find((p) => p.mapTypeKey === "NORMAL"),
      relative: pollutants.find((p) => p.mapTypeKey === "RELATIVE_DIFFERENCE"),
    };
  });
}

function LoadData(props: Props) {
  const { case: caseBase } = props;
  const { data: info } = useCaseInfo(caseBase.id);

  const { t } = useTranslation();

  function formatRelativeDifference(value: number) {
    const percentage = round(value * 100, 2);
    const sign = percentage > 0 ? "+" : "";

    if (Math.abs(percentage) < 0.01) {
      return {
        text: "0%",
      };
    }

    return {
      text: `${sign}${percentage}%`,
      color: percentage > 0 ? "error" : "success",
    };
  }

  if (!info?.scenarioInfoList || info.scenarioInfoList.length === 0) {
    return (
      <Typography className="description" component="div" variant="body2" color="text.secondary">
        {t("label.noFurtherInfoAvailableForThisCase")}
      </Typography>
    );
  }

  return (
    <div className="scenarios-container">
      {info.scenarioInfoList.map((scenario) => {
        const pollutants = groupPollutants(scenario.pollutantList);

        if (pollutants.length === 0) return null;

        return (
          <Fragment key={scenario.id}>
            <div className="scenario-card">
              <Typography variant="h6" className="scenario-name">
                {scenario.name}
              </Typography>
              <div className="pollutants-container">
                {pollutants.map((group) => {
                  const normal = group.normal;
                  const relative = group.relative;
                  if (!normal) return null;

                  const minFormatted = relative ? formatRelativeDifference(relative.minMean) : null;
                  const maxFormatted = relative ? formatRelativeDifference(relative.maxMean) : null;

                  return (
                    <TableContainer key={group.substanceId} className="pollutant-card">
                      <Table size="small" className="pollutant-table">
                        <TableHead>
                          <TableRow>
                            <TableCell className="pollutant-name-cell">
                              {/* biome-ignore lint/security/noDangerouslySetInnerHtml: HTML content from API */}
                              <span
                                className="pollutant-name"
                                dangerouslySetInnerHTML={{ __html: normal.substanceName }}
                              />
                              <span>({normal.substanceUnit})</span>
                            </TableCell>
                            <TableCell>{t("label.min")}</TableCell>
                            <TableCell>{t("label.max")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell className="label-cell">{t("label.annualMean")}</TableCell>
                            <TableCell className="value-cell">
                              <Tooltip title={round(normal.minMean, 2).toLocaleString("nl-BE")} disableInteractive>
                                <Typography>{round(normal.minMean, 2).toLocaleString("nl-BE")}</Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell className="value-cell">
                              <Tooltip title={round(normal.maxMean, 2).toLocaleString("nl-BE")} disableInteractive>
                                <Typography>{round(normal.maxMean, 2).toLocaleString("nl-BE")}</Typography>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="label-cell">{t("label.relativeDifference")}</TableCell>
                            <TableCell className="value-cell">
                              {minFormatted && (
                                <Tooltip title={minFormatted.text} disableInteractive>
                                  <Typography className={`${minFormatted.color}`}>{minFormatted.text}</Typography>
                                </Tooltip>
                              )}

                              {!minFormatted && <Typography>-</Typography>}
                            </TableCell>
                            <TableCell className="value-cell">
                              {maxFormatted && (
                                <Tooltip title={maxFormatted.text} disableInteractive>
                                  <Typography className={`${maxFormatted.color}`}>{maxFormatted.text}</Typography>
                                </Tooltip>
                              )}
                              {!maxFormatted && <Typography>-</Typography>}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  );
                })}
              </div>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

const Styles = styled(TableRow)`
  .description {
    padding-bottom: ${(props) => props.theme.spacing(2)};
    padding-left: ${(props) => props.theme.spacing(6)};
    padding-top: ${(props) => props.theme.spacing(2)};
    overflow-wrap: break-word;
  }
  .parent-cell {
    padding: 16px;
    max-width: 0;
  }
  .loading-spinner {
    margin: 16px;
    margin-left: 70px;
  }
  .scenarios-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${(props) => props.theme.spacing(2)};
    padding: ${(props) => props.theme.spacing(0, 2)};
    padding-bottom: ${(props) => props.theme.spacing(3)};
  }
  .MuiCircularProgress-root {
    margin-right: auto;
    margin-left: ${(props) => props.theme.spacing(6)};
  }
  .scenario-card {
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.theme.spacing(1)};
    padding: ${(props) => props.theme.spacing(2, 4)};
    border-radius: 8px;
    background-color: #fff;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .scenario-name {
    margin-bottom: ${(props) => props.theme.spacing(1)};
  }

  .pollutants-container {
    display: grid;

    grid-template-columns: 1fr;
    gap: ${(props) => props.theme.spacing(2)};
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    ${({ theme }) => theme.breakpoints.up("lg")} {
      grid-template-columns: repeat(2, 1fr);
    }
    ${({ theme }) => theme.breakpoints.up("xl")} {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .pollutant-card {
    background-color: #f5f5f5;
    padding: ${(props) => props.theme.spacing(0.5, 1)};
    border-radius: 8px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  .pollutant-name {
    color: ${({ theme }) => theme.palette.primary.main};
    margin-right: 6px;
    font-weight: 600;
    font-size: 14px;
  }

  .pollutant-table {
    /* margin-top: ${(props) => props.theme.spacing(1)};
    table-layout: fixed;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box; */
  }

  .MuiTableBody-root {
    background-color: transparent;
  }

  .MuiTableCell-root {
    padding: ${(props) => props.theme.spacing(0.5, 1)};
    border: none;
    background-color: transparent;
  }

  .MuiTableHead-root {
    background-color: transparent;
  }

  .MuiTableHead-root .MuiTableCell-root {
    font-size: 11px;
    font-weight: 500;
  }

  .pollutant-name-cell {
    width: 50%;
  }

  .label-cell {
    font-weight: 500;
    white-space: nowrap;
    width: 50%;
    font-size: 12px;
  }

  .MuiTableBody-root .MuiTableCell-root:not(.label-cell),
  .MuiTableHead-root .MuiTableCell-root:not(.pollutant-name-cell) {
    min-width: 0;
    width: 25%;
  }

  .value-cell {
    .MuiTypography-root {
      display: block;
      white-space: nowrap;
      overflow: hidden;
      font-size: 12px;
      text-overflow: ellipsis;

      &.success {
        color: green;
      }

      &.error {
        color: ${({ theme }) => theme.palette.error.main};
      }
    }
  }
`;

// const Styles = styled(TableRow)`
//   .no-padding {
//     padding: 0;
//     border-bottom: 1px solid rgba(224, 224, 224, 1);
//   }

//   .scenarios-container {
//     display: flex;
//     flex-direction: column;
//     padding: ${({ theme }) => theme.spacing(2)} 0;
//     overflow-x: auto;
//   }

//   .scenario-table {
//     table-layout: fixed;
//     width: 100%;
//     &:last-of-type {
//       border-bottom: none;
//     }
//   }

//   .MuiDivider-root {
//     margin: 16px;
//   }

//   .MuiTableHead-root,
//   .MuiTableBody-root {
//     background-color: transparent;
//   }

//   .MuiTableCell-root {
//     border: none;
//     padding: 6px 4px;
//   }

//   .label-cell {
//     width: 150px;
//   }
// `;
